import React, { useMemo } from 'react';
import Layout from '../components/Layout';
import { useNavigate } from 'react-router-dom';
import { Plus, RefreshCw, Filter, Download, Clock, User } from 'lucide-react';
import "react-datepicker/dist/react-datepicker.css";

// Hooks personnalisés
import useAssiduite from '../hooks/useAssiduite';

// Composants d'assiduité
import AssiduiteFilters from '../components/assiduites/AssiduiteFilters';
import AssiduiteTable from '../components/assiduites/AssiduiteTable';
import AssiduiteForm from '../components/assiduites/AssiduiteForm';
import AssiduitePagination from '../components/assiduites/AssiduitePagination';
import EmptyState from '../components/assiduites/EmptyState';
import StatistiquesRetards from '../components/assiduites/StatistiquesRetards';
import AssiduiteSearchBar from '../components/assiduites/AssiduiteSearchBar';
import ConfirmationModal from '../components/ConfirmationModal';

// Hook pour les données d'enseignant
import useEnseignantData from '../hooks/useEnseignantData';

const AssiduitePage = () => {
  const navigate = useNavigate();
  
  // Hook pour gérer les données spécifiques aux enseignants
  const { resetEnseignantData } = useEnseignantData();

  // États pour la recherche
  const [searchTerm, setSearchTerm] = React.useState('');
  const [searchFilters, setSearchFilters] = React.useState({
    dateDebut: '',
    dateFin: '',
    classe: '',
    module: '',
    tempsMin: '',
    tempsMax: ''
  });
  const [showAdvancedFilters, setShowAdvancedFilters] = React.useState(false);

  // Hook principal pour toute la logique métier
  const {
    // States
    activeTab,
    showModal,
    showDeleteModal,
    itemToDelete,
    isDeleting,
    isEditing,
    retardsEtudiants,
    retardsEnseignants,
    loading,
    classes,
    niveaux,
    filieres,
    modules,
    etudiants,
    enseignants,
    selectedClasse,
    selectedModule,
    currentPage,
    totalPages,
    showFilters,
    filters,
    formData,
    
    // Setters
    setActiveTab,
    setShowModal,
    setShowDeleteModal,
    setSelectedClasse,
    setSelectedModule,
    setCurrentPage,
    setShowFilters,
    setFilters,
    setFormData,
    
    // Actions
    handleSubmit,
    handleExport,
    handleDelete,
    confirmDelete,
    handleEdit,
    formatDate,
    resetForm
  } = useAssiduite();



  // Fonction pour changer d'onglet avec reset des données d'enseignant
  const handleTabChange = (newTab) => {
    if (newTab !== activeTab) {
      resetEnseignantData(); // Reset les données d'enseignant
      resetForm(); // Reset le formulaire
    }
    setActiveTab(newTab);
  };

  const handleRefresh = () => {
    // Déclencher le rafraîchissement des données
    window.location.reload();
  };

  // Optimisation du rendu avec useMemo
  const currentData = useMemo(() => {
    return activeTab === 'etudiants' ? retardsEtudiants : retardsEnseignants;
  }, [activeTab, retardsEtudiants, retardsEnseignants]);

  // Fonction de filtrage des données
  const filteredData = useMemo(() => {
    let filtered = currentData;

    // Filtrage par terme de recherche
    if (searchTerm) {
      filtered = filtered.filter(item => {
        const nom = activeTab === 'etudiants' 
          ? `${item.etudiant?.nom || ''} ${item.etudiant?.prenom || ''}`
          : `${item.enseignant?.nom || ''} ${item.enseignant?.prenom || ''}`;
        
        return nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
               (item.classe_nom || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
               (item.module_nom || item.matiere?.libelle || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
               (item.motif || '').toLowerCase().includes(searchTerm.toLowerCase());
      });
    }

    // Filtres avancés
    if (searchFilters.dateDebut) {
      filtered = filtered.filter(item => new Date(item.date_retard || item.date) >= new Date(searchFilters.dateDebut));
    }
    if (searchFilters.dateFin) {
      filtered = filtered.filter(item => new Date(item.date_retard || item.date) <= new Date(searchFilters.dateFin));
    }
    if (searchFilters.classe) {
      filtered = filtered.filter(item => item.classe_id === parseInt(searchFilters.classe));
    }
    if (searchFilters.module) {
      filtered = filtered.filter(item => item.module_id === parseInt(searchFilters.module));
    }
    if (searchFilters.tempsMin) {
      filtered = filtered.filter(item => item.temps >= parseInt(searchFilters.tempsMin));
    }
    if (searchFilters.tempsMax) {
      filtered = filtered.filter(item => item.temps <= parseInt(searchFilters.tempsMax));
    }

    return filtered;
  }, [currentData, searchTerm, searchFilters, activeTab]);

  // Vérification si on doit afficher l'état vide
  const showEmptyState = useMemo(() => {
    return filteredData.length === 0;
  }, [filteredData.length]);

  // Vérification si on doit afficher le tableau
  const showTable = useMemo(() => {
    return filteredData.length > 0;
  }, [filteredData.length]);

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#202654]"></div>
            <span className="ml-3 text-gray-600">Chargement des données...</span>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
          {/* Card principale avec design professionnel */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            {/* Header avec bannière bleue */}
            <div className="bg-gradient-to-r from-[#202654] to-[#202654]/90 px-6 py-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                <div className="flex items-center text-white">
                  <button
                    onClick={() => navigate(-1)}
                    className="mr-4 p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-all duration-200 backdrop-blur-sm"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                  <div>
                    <h1 className="text-2xl font-bold">Gestion de l'Assiduité</h1>
                    <p className="mt-1 text-blue-100">
                      Gérez les retards des étudiants et enseignants
                    </p>
                  </div>
                </div>
                <div className="mt-4 md:mt-0 flex flex-col sm:flex-row sm:space-x-3 space-y-3 sm:space-y-0">
                  <button
                    onClick={handleRefresh}
                    className="inline-flex items-center justify-center px-4 py-2 border border-white/20 rounded-md shadow-sm text-sm font-medium text-white bg-white/10 hover:bg-white/20 backdrop-blur-sm transition-all duration-200"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Actualiser
                  </button>
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="inline-flex items-center justify-center px-4 py-2 border border-white/20 rounded-md shadow-sm text-sm font-medium text-white bg-white/10 hover:bg-white/20 backdrop-blur-sm transition-all duration-200"
                  >
                    <Filter className="h-4 w-4 mr-2" />
                    Filtres
                  </button>
                  <button
                    onClick={() => setShowModal(true)}
                    className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-white/10 hover:bg-white/20 backdrop-blur-sm transition-all duration-200"
                  >
                    <Plus className="h-5 w-5 mr-2" />
                    Nouveau Retard
                  </button>
                </div>
              </div>
            </div>

            {/* Onglets intégrés dans le design */}
            <div className="border-b border-gray-200 bg-white">
              <nav className="flex">
                <button
                  className={`px-6 py-3 text-sm font-semibold focus:outline-none transition-all duration-200 ${
                    activeTab === 'etudiants' 
                      ? 'border-b-4 border-[#202654] text-[#202654] bg-white rounded-t-lg shadow-sm' 
                      : 'text-gray-500 hover:text-[#202654] hover:bg-gray-100'
                  }`}
                  style={{ marginRight: '-2px' }}
                  onClick={() => handleTabChange('etudiants')}
                >
                  <User className="h-4 w-4 mr-2 inline" />
                  Retards Étudiants
                </button>
                <button
                  className={`px-6 py-3 text-sm font-semibold focus:outline-none transition-all duration-200 ${
                    activeTab === 'enseignants' 
                      ? 'border-b-4 border-[#202654] text-[#202654] bg-white rounded-t-lg shadow-sm' 
                      : 'text-gray-500 hover:text-[#202654] hover:bg-gray-100'
                  }`}
                  onClick={() => handleTabChange('enseignants')}
                >
                  <Clock className="h-4 w-4 mr-2 inline" />
                  Retards Enseignants
                </button>
              </nav>
            </div>



            {/* Filtres avec animation */}
          {showFilters && (
              <div className="animate-in slide-in-from-top duration-200">
            <AssiduiteFilters
              filters={filters}
              setFilters={setFilters}
              setShowFilters={setShowFilters}
              classes={classes}
              niveaux={niveaux}
              filieres={filieres}
                />
              </div>
            )}

            {/* Section des données */}
            <div className="p-6">
              {/* Barre de recherche */}
              <div className="mb-4">
                <AssiduiteSearchBar
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                  searchFilters={searchFilters}
                  setSearchFilters={setSearchFilters}
                  activeTab={activeTab}
                  classes={classes}
                  modules={modules}
                  showAdvancedFilters={showAdvancedFilters}
                  setShowAdvancedFilters={setShowAdvancedFilters}
                />
                
                {/* Indicateur de résultats */}
                {(searchTerm || Object.values(searchFilters).some(filter => filter)) && (
                  <div className="mt-2 text-sm text-gray-600">
                    {filteredData.length} résultat{filteredData.length !== 1 ? 's' : ''} trouvé{filteredData.length !== 1 ? 's' : ''}
                  </div>
                )}
              </div>

              {/* État vide */}
            {showEmptyState && (
              <EmptyState 
                activeTab={activeTab}
                onNewRetard={() => setShowModal(true)}
              />
            )}

            {/* Tableau des retards (si données disponibles) */}
            {showTable && (
          <AssiduiteTable
            activeTab={activeTab}
                  retardsEtudiants={filteredData}
                  retardsEnseignants={filteredData}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
            formatDate={formatDate}
          />
            )}

            {/* Pagination conditionnelle */}
            {totalPages > 1 && (
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-t border-gray-200 mt-6">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Page <span className="font-medium">{currentPage}</span> sur{' '}
                    <span className="font-medium">{totalPages}</span>
                  </div>
          <AssiduitePagination
            currentPage={currentPage}
            totalPages={totalPages}
            setCurrentPage={setCurrentPage}
          />
                </div>
                    </div>
                  )}
                  </div>

            {/* Section des statistiques */}
            <div className="bg-gray-50 border-t border-gray-200 p-6">
              <StatistiquesRetards
                retardsEtudiants={retardsEtudiants}
                retardsEnseignants={retardsEnseignants}
                activeTab={activeTab}
              />
            </div>
          </div>


                  </div>

        {/* Formulaire d'ajout/modification */}
        <AssiduiteForm
          showModal={showModal}
          setShowModal={setShowModal}
          activeTab={activeTab}
          formData={formData}
          setFormData={setFormData}
          selectedClasse={selectedClasse}
          setSelectedClasse={setSelectedClasse}
          selectedModule={selectedModule}
          setSelectedModule={setSelectedModule}
          classes={classes}
          etudiants={etudiants}
          enseignants={enseignants}
          modules={modules}
          handleSubmit={handleSubmit}
          resetForm={resetForm}
          isEditing={isEditing}
        />

        {/* Modal de confirmation pour la suppression */}
        <ConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
          }}
          onConfirm={confirmDelete}
          title="Supprimer le retard"
          message="Êtes-vous sûr de vouloir supprimer ce retard ? Cette action est irréversible."
          confirmText="Supprimer"
          cancelText="Annuler"
          type="danger"
          isLoading={isDeleting}
        />
      </div>
    </Layout>
  );
};

export default AssiduitePage;